import { useState } from "react";
import styled from "styled-components";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { AnimationDefinition } from "framer-motion/types/render/utils/animation";
import { Box } from "../Box";

interface DateCycleProps {
  onComplete?: () => any;
}

const durationFactor2 = (
  index: number,
  length: number,
  factor: number = 0.7
): number => {
  return 1 - easeOutCirc(index / length) * factor;
};

const durM = 0.6;
const durS = 0.2;

const DateContainer = styled(motion.div)`
  position: relative;
  overflow: hidden;
  height: 26vw;
`;

const Date = styled(motion(Box))`
  position: absolute;
  left: 0;
  font-size: 26vw;
  font-weight: 700;
  display: inline-block;
  /* letter-spacing: -1vw; */
`;

const DateChar = styled(motion.span)`
  display: inline-block;
`;

const dates = [
  "1841",
  "1868",
  "1891",
  "1908",
  // "1911",
  "1921",
  "1930",
  // "1940",
  "1948",
  // "1950",
  "1952",
  // "1961",
  "1967",
  // "1969",
  "1982",
  // "1985",
  "2005",
  "2020",
];

const currentDateVariants: Variants = {
  animate: (index) =>
    index === 0
      ? {
          transition: {
            staggerChildren: durS * durationFactor2(index, dates.length, 0.7),
            delayChildren: 2,
          },
        }
      : { transition: { delay: 0, duration: 0 } },
  exit: (index) => ({
    transition: {
      // delay: 0,
      staggerChildren: durS * durationFactor2(index, dates.length, 0.7),
    },
  }),
};

const nextDateVariants: Variants = {
  exit: (index) => ({
    transition: {
      staggerChildren: durS * durationFactor2(index, dates.length, 0.7),
    },
  }),
};

const dateCharVariants: Variants = {
  initial: (index) =>
    index === 0
      ? {
          y: "100%",
        }
      : {
          y: 0,
        },
  animate: (index) =>
    index === 0
      ? {
          y: 0,
          transition: {
            type: "spring",
            duration: durM * durationFactor2(index, dates.length),
            bounce: 0.2,
          },
        }
      : {
          y: 0,
        },
  exit: (index) =>
    index !== dates.length
      ? {
          y: "-100%",
          transition: {
            type: "spring",
            duration: durM * durationFactor2(index, dates.length),
            bounce: 0.2,
          },
        }
      : {
          y: 0,
        },
};

const finalDateVariants: Variants = {
  animate: {
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.7,
    },
  },
};

const finalDateCharVariants: Variants = {
  initial: {
    y: "-100%",
  },
  animate: {
    y: 0,
    transition: {
      type: "spring",
      duration: durM,
    },
  },
};

export const DateCycle: React.FC<DateCycleProps> = ({ onComplete }) => {
  const [dateIndex, setDateIndex] = useState(0);
  const [showFinalDate, setShowFinalDate] = useState(false);

  const onAnimationComplete = (def: AnimationDefinition) => {
    if (dateIndex === dates.length - 1) {
      onComplete && onComplete();
      setShowFinalDate(true);
    }
    if (dateIndex < dates.length && def === "animate") {
      setDateIndex(dateIndex + 1);
    }
    // if (dateIndex === dates.length && def === "animate") {
    //   setShowFinalDate(true);
    // }
  };

  return (
    <DateContainer>
      <AnimatePresence exitBeforeEnter>
        {dates[dateIndex] && (
          <Date
            key={dates[dateIndex] + 0}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={currentDateVariants}
            custom={dateIndex}
            onAnimationComplete={onAnimationComplete}
            top={0}
          >
            {Array.from(dates[dateIndex]).map((char, i) => (
              <DateChar
                variants={dateCharVariants}
                key={char + i + 0}
                custom={dateIndex}
              >
                {char}
              </DateChar>
            ))}
          </Date>
        )}
      </AnimatePresence>
      <AnimatePresence exitBeforeEnter>
        {dates[dateIndex + 1] && (
          <Date
            key={dates[dateIndex] + 1}
            initial={false}
            animate="animate"
            exit="exit"
            variants={nextDateVariants}
            custom={dateIndex}
            top={"100%"}
          >
            {Array.from(dates[dateIndex + 1]).map((char, i) => (
              <DateChar
                variants={dateCharVariants}
                key={char + i + 1}
                custom={dateIndex}
              >
                {char}
              </DateChar>
            ))}
          </Date>
        )}
      </AnimatePresence>
      <AnimatePresence exitBeforeEnter>
        {showFinalDate && (
          <Date
            initial="initial"
            animate="animate"
            exit="exit"
            variants={finalDateVariants}
            top={0}
          >
            {Array.from("1841").map((char, i) => (
              <DateChar variants={finalDateCharVariants} key={char + i + 2}>
                {char}
              </DateChar>
            ))}
          </Date>
        )}
      </AnimatePresence>
    </DateContainer>
  );
};

function easeOutExpo(x: number): number {
  return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
}

function easeOutQuart(x: number): number {
  return 1 - Math.pow(1 - x, 4);
}

function easeInOutCubic(x: number): number {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

function easeInOutSine(x: number): number {
  return -(Math.cos(Math.PI * x) - 1) / 2;
}

function easeInQuart(x: number): number {
  return x * x * x * x;
}

function easeOutCirc(x: number): number {
  return Math.sqrt(1 - Math.pow(x - 1, 2));
}

function easeInOutExpo(x: number): number {
  return x === 0
    ? 0
    : x === 1
    ? 1
    : x < 0.5
    ? Math.pow(2, 20 * x - 10) / 2
    : (2 - Math.pow(2, -20 * x + 10)) / 2;
}
